import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@arcadeai/design-system/components/ui/atoms/alert";
import { ScrollArea } from "@arcadeai/design-system/components/ui/atoms/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@arcadeai/design-system/components/ui/atoms/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tabs";
import { isPrebuiltProvider } from "@arcadeai/design-system/metadata/utils";
import { AlertCircle } from "lucide-react";
import { useEffect, useState } from "react";
import type { OAuthProviderItem } from "@/app/oauth/types";
import { generateExternalId } from "@/app/oauth/utils";
import { CustomForm } from "./custom-form/custom-form";
import { PrebuiltForm } from "./prebuilt-form/prebuilt-form";

type OAuthSheetContentProps = {
  externalId: string;
  handleClose: () => void;
  oauth?: OAuthProviderItem;
};

const OAuthSheetContentEditing = ({
  externalId,
  handleClose,
  oauth,
}: OAuthSheetContentProps & { oauth: OAuthProviderItem }) => {
  const isPrebuilt = isPrebuiltProvider(oauth.provider_id);

  const Form = isPrebuilt ? PrebuiltForm : CustomForm;

  return (
    <Form
      freshExternalId={externalId}
      handleClose={handleClose}
      oauth={oauth}
    />
  );
};

const OAuthSheetContentCreating = ({
  externalId,
  handleClose,
  oauth,
}: OAuthSheetContentProps) => (
  <Tabs className="w-full pb-4" defaultValue="prebuilt">
    <TabsList className="grid w-full grid-cols-2">
      <TabsTrigger value="prebuilt">Included Providers</TabsTrigger>
      <TabsTrigger value="custom">Custom Provider</TabsTrigger>
    </TabsList>

    <TabsContent value="prebuilt">
      <PrebuiltForm
        freshExternalId={externalId}
        handleClose={handleClose}
        oauth={oauth}
      />
    </TabsContent>

    <TabsContent value="custom">
      <CustomForm
        freshExternalId={externalId}
        handleClose={handleClose}
        oauth={oauth}
      />
    </TabsContent>
  </Tabs>
);

type OAuthSheetProps = {
  oauth?: OAuthProviderItem;
  trigger: React.ReactElement;
  /** When false, the existing provider is shown as a locked, view-only form. */
  canEdit?: boolean;
};

export const OAuthSheet = ({
  oauth,
  trigger,
  canEdit = true,
}: OAuthSheetProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Generate external ID each time sheet opens for new providers
  const [externalId, setExternalId] = useState("");

  useEffect(() => {
    // Generate new ID when sheet opens for creating (not editing)
    if (isOpen && !oauth) {
      setExternalId(generateExternalId());
    }
  }, [isOpen, oauth]);

  const isStatic = oauth?.binding.type === "static";
  // An existing provider is read-only when it's config-file-defined (static) or
  // the caller lacks update permission.
  const isReadOnly = !!oauth && (isStatic || !canEdit);
  const handleClose = () => setIsOpen(false);

  return (
    <Sheet onOpenChange={setIsOpen} open={isOpen}>
      <SheetTrigger render={trigger} />
      <SheetContent className="w-full px-2">
        <ScrollArea className="h-full w-full">
          <SheetHeader>
            <SheetTitle className="mt-2">OAuth Provider</SheetTitle>
            <SheetDescription>
              Configure OAuth provider settings.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-4 h-full px-4">
            {isReadOnly && (
              <Alert className="mb-6 border-orange-500" variant="warning">
                <AlertCircle className="size-4" />
                <AlertTitle>
                  {isStatic ? "Static OAuth Provider" : "Read-only access"}
                </AlertTitle>
                <AlertDescription className="mt-2">
                  {isStatic
                    ? "These settings are locked for editing and can only be updated through your configuration file."
                    : "You don't have permission to edit this provider. Contact your organization admin to request access."}
                </AlertDescription>
              </Alert>
            )}
            {oauth ? (
              // A disabled fieldset locks every control and the submit button at
              // once, covering both the prebuilt and custom forms. It also
              // disables the custom form's advanced-section accordions, so those
              // stay collapsed in read-only mode.
              <fieldset
                className="m-0 min-w-0 border-0 p-0"
                disabled={isReadOnly}
              >
                <OAuthSheetContentEditing
                  externalId={externalId}
                  handleClose={handleClose}
                  oauth={oauth}
                />
              </fieldset>
            ) : (
              <OAuthSheetContentCreating
                externalId={externalId}
                handleClose={handleClose}
                oauth={oauth}
              />
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};
